/*
<purpose>
  Upload + process page: Schnell-Upload drop zone (left column) + Konfiguration panel
  (right column). The Eingereicht list is server-side cursor-paginated via
  useActivePagination(bucket:'uploads'), with a sort dropdown (uploaded | received |
  updated | rejections) + direction flip, and infinite-scroll sentinel — matching the
  AntwortenSeite inline-row pattern.
</purpose>
<dependencies>
  useActivePagination (server-side paginated rows + counts for 'uploads' bucket);
  useTicketManager (tm.* mutators for API calls + in-flight tracking);
  smarttix.ingestTicket / getTicket / processTicket / deleteTicket / bulkProcess;
  ContextPanel (right column — field verification, OCR overrides, force-category, Verarbeiten action).
</dependencies>
<constraints>
  - hook removeRow/reset give optimistic local UI without a full refetch.
  - Auto-button operates on loaded hook rows (paginated set), not the full server count.
  - PII reveal-gate (canReveal) applies to submitted/ingested only — same rule as before.
  - Per-row unread dot on Uploads/Eingereicht rows: DB-backed via useReadState (#6).
    Opening a row calls markRead() optimistically.
  - uploadProgress + uploadAbortRef are App-level props (lifted Phase 2 Task 15) so
    navigation away during a batch upload doesn't lose the indicator or AbortController.
  - UI Layout Stability: space for conditional elements pre-allocated; new elements fade in.
  - Verarbeiten (handleProcess) hides its row optimistically the instant it's clicked, keyed off
    processHiddenUuids — a page-local Set written only by this page's own handleProcess, scoped to
    the user's click in THIS view (opacity+max-height collapse, no array splice until real
    success). It is deliberately NOT keyed off tm.processingUuids: that Set signals in-flight
    processing app-wide (Dashboard Schnell-Upload, Testgenerator's Bolt, and this page all write to
    it) and tm never clears it on success (see TicketManagerContext) — so a ticket processed
    elsewhere and merely passing through the Uploads list would render permanently hidden here with
    no click of its own to explain it and no path back. The local Set holds hidden through a
    cold-start fast API resolution (see PROCESS_FADE_MS) until handleProcess has waited out the
    remainder of the fade and actually spliced the row via hook.removeRow — no mid-collapse
    unmount, no sibling snap. ANY failure — structured or thrown — must bring the row back with a
    visible error state; processErrorUuids both renders that state and forces the row visible again
    regardless of processHiddenUuids, which also covers the (defensive-only) thrown-failure path
    (in practice services/api.ts's processTicket always resolves {success:false}, never throws, but
    the override keeps the contract correct either way).
  - Ghost-reappear fix: a uuid is NEVER cleared out of processHiddenUuids just because handleProcess
    finished its own local hook.removeRow — that clear-on-success used to be the bug. The uploads
    list is re-derived from server data on every background poll (POLLING_INTERVAL_MS →
    hook.refresh(), see useActivePagination), and the just-processed ticket's server-side status can
    still be mid-transition when that poll lands — refresh() then re-inserts the SAME uuid into
    uploadRows even though this page already spliced it locally. If the hidden flag had already been
    cleared at that point, the reinsertion rendered fully visible — the row visibly popping back in
    right as/after the fade finished. Fix: a dedicated garbage-collection effect (keyed on
    hook.counts — set ONLY by a genuine server fetch, never by local removeRow/patchRow, so this
    page's own splice can't fake a "ticket left the data" signal) is now the ONLY place a hidden
    uuid is forgotten on the success path — it prunes an entry once, and only once, that uuid stops
    appearing in the fetched data at all. Until then the uuid stays hidden no matter how many
    refetches keep including it.
</constraints>
*/
import React, { useState, useEffect, useRef, useMemo } from 'react';
import DOMPurify from 'dompurify';
import {
  BentoCard, TriStateControl, PanelSection, styleRedactionTokens, escapeHtmlText,
  isHtml, PaperclipIcon, TrashIcon, RevealContext, SelectionToggleIcon, CrossIcon,
  AutoIcon, LightningIcon, SortDirectionIcon, KopiertIcon, ReanalysisIcon,
  shouldShowAttachmentGlyph, mayShowAttachmentGlyph, AttachmentGlyphSlot, SkeletonBlock,
} from './shared';
import { AutoButton } from './AutoButton';
import { InfoTooltip } from './InfoTooltip';
import { SelectionStrip } from './SelectionStrip';
import { RueckgaengigToast } from './RueckgaengigToast';
import { CountUpNumber } from './CountUpNumber';
import { useSelectionMode } from '../hooks/useSelectionMode';
import { useActivePagination, ActiveFilters, ActiveSort, ActiveDir } from '../hooks/useActivePagination';
import { formatRelativeTime } from '../utils/dateFormatting';
import { theme } from '../theme';
import {
  UPLOAD_THROTTLE_MS, POLLING_INTERVAL_MS,
  CATEGORY_KEYS, CATEGORY_LABELS,
  REASON_CODES, SECONDARY_AXIS_MAX_VISIBLE,
  OCR_OPTIONS,
} from '../constants';
import { smarttix, UiWarps } from '../services/api';
import { TicketDetail, ProcessContext, AttachmentInfo } from '../types';
import { useTicketManager } from '../contexts/TicketManagerContext';
import { PageType } from './NavBar';
import { useMaintenanceMode } from '../contexts/MaintenanceContext';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { useReadState } from '../hooks/useReadState';
import { listDetailFlex } from '../services/listDetailRatio';
import { useListDetailFlip } from '../context/ListDetailFlipContext';
import { useTicketDetailRefresh, TRANSIENT_STATUSES } from '../hooks/useTicketDetailRefresh';
import { resolveAttachmentOcrRows, partitionAttachmentRows, countAttachments, ocrReasonGroupHeading, AttachmentOcrRow } from '../services/attachmentOcrStatus';

// Why: module-level cache so OCR/field/context overrides survive selection changes and navigation
const ticketContextCache = new Map<string, {
  processContext: ProcessContext;
  ocrOverrides: Record<string, 'force' | 'auto' | 'skip'>;
  fieldStates: Record<string, 'verified' | 'unverified' | 'incorrect'>;
  fieldOverrides: Record<string, string>;
}>();

// Single source for the Verarbeiten row's collapse duration — used both in the row's own
// opacity/max-height transition (below) and by handleProcess to know how long to wait out after a
// fast API resolution before it's safe to splice the row for real (finding: cold-start resolutions
// can beat the fade, unmounting mid-collapse and snapping the row below it up into its place).
export const PROCESS_FADE_MS = 250;

// ═════════════════════════════════════════════════════════
// UploadsSeite
// ═════════════════════════════════════════════════════════
const UploadsSeite: React.FC<{
  initialTicket?: string | null;
  onNavigate?: (page: PageType, params?: Record<string, string>) => void;
  uiWarps: UiWarps;
  uploadProgress: { current: number; total: number } | null;
  setUploadProgress: React.Dispatch<React.SetStateAction<{ current: number; total: number } | null>>;
  uploadAbortRef: React.MutableRefObject<AbortController | null>;
  // Tenant-wide inline-image counting rule, threaded from App's single /config read — a per-host
  // config read is forbidden, so this arrives as a prop like showSourceChannel elsewhere. ABSENT ⇒
  // true (see ContextPanel's default) — an omitted prop must reproduce the behaviour that shipped
  // before this rule existed, never a silent flip to the new posture.
  attachmentsCountInline?: boolean;
}> = ({ initialTicket, onNavigate, uiWarps, uploadProgress, setUploadProgress, uploadAbortRef, attachmentsCountInline }) => {
  const tm = useTicketManager();
  const cm = useCategoryMeta();
  const { isRead, markRead } = useReadState();

  // ─── Pagination (uploads bucket) ────────────────────
  // UploadsSeite default: most-recently-uploaded first (standard for the ingest queue).
  const [sort, setSort] = useState<ActiveSort>('uploaded');
  const [dir, setDir] = useState<ActiveDir>('desc');
  const filters: ActiveFilters = useMemo(() => ({}), []);
  const hook = useActivePagination('uploads', sort, filters, dir);
  const uploadRows = hook.rows;

  // ─── Selection / UI state ────────────────────────────
  const sel = useSelectionMode();
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<TicketDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showAutoModal, setShowAutoModal] = useState(false);
  const [autoSubmitting, setAutoSubmitting] = useState(false);
  const [infoToast, setInfoToast] = useState<string | null>(null);
  const [successToast, setSuccessToast] = useState<{ count: number } | null>(null);

  // ─── Optimistic Verarbeiten (process) row state ──────
  // Both Sets are page-local and click-scoped — deliberately NOT keyed off tm.processingUuids,
  // which is a global in-flight tracker written by every page that calls tm.processTicket
  // (Dashboard, Testgenerator, this page) and never cleared on success (see TicketManagerContext).
  // processHiddenUuids: rows this page's own handleProcess just hid optimistically.
  // processErrorUuids: rows that failed and need their error line + forced reappearance (see
  // PROCESS_FADE_MS below for why a successful resolve still waits before the row is spliced out
  // for real).
  const [processHiddenUuids, setProcessHiddenUuids] = useState<Set<string>>(new Set());
  const [processErrorUuids, setProcessErrorUuids] = useState<Set<string>>(new Set());

  // Garbage-collect processHiddenUuids: a hidden uuid is forgotten ONLY once a genuine SERVER fetch
  // (initial load, loadMore, or the background poll's refresh()) stops reporting it — never merely
  // because handleProcess's own local hook.removeRow spliced it out of `rows` optimistically. That
  // distinction is the whole fix: this effect is keyed on hook.counts, not on uploadRows/hook.rows.
  // useActivePagination sets counts on every real fetchPage()/refresh() call and NEVER touches it
  // from removeRow/patchRow — so counts changing is a clean, fetch-only signal, immune to our own
  // optimistic splice. (Keying on uploadRows instead was tried and is wrong: hook.removeRow's own
  // splice makes the uuid transiently absent from uploadRows, which would fire the prune the
  // instant handleProcess calls it — emptying processHiddenUuids just before the very poll this
  // fix exists to survive re-inserts the row with nothing left to hide it.)
  // Root cause this closes: the background poll (POLLING_INTERVAL_MS → hook.refresh()) can land
  // before the ticket's server-side status has actually left the uploads bucket, re-inserting the
  // SAME uuid into uploadRows right after handleProcess already spliced it locally. Clearing the
  // hidden flag at splice-time (the old bug) left nothing to suppress that reinsertion, so the row
  // visibly popped back in right as/after the fade finished. Keeping the flag alive for as long as
  // the uuid keeps showing up in fetched responses — and pruning it only once a fetch genuinely
  // stops including it — makes "hidden" durable across any number of intervening refreshes.
  useEffect(() => {
    setProcessHiddenUuids(prev => {
      if (prev.size === 0) return prev;
      const stillPresent = new Set(uploadRows.map(r => r.uuid));
      let changed = false;
      const next = new Set<string>();
      prev.forEach(uuid => {
        if (stillPresent.has(uuid)) next.add(uuid); else changed = true;
      });
      return changed ? next : prev;
    });
    // uploadRows deliberately excluded — see the comment above; this must only re-evaluate on a
    // genuine fetch (counts), reading whatever uploadRows holds AT that fetch's render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hook.counts]);

  // ─── Per-ticket context (cached across selection changes) ─
  const [processContext, setProcessContext] = useState<ProcessContext>({});
  const [ocrOverrides, setOcrOverrides] = useState<Record<string, 'force' | 'auto' | 'skip'>>({});
  const [fieldStates, setFieldStates] = useState<Record<string, 'verified' | 'unverified' | 'incorrect'>>({});
  const [fieldOverrides, setFieldOverrides] = useState<Record<string, string>>({});

  // ─── Responsive ──────────────────────────────────────
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const isCompact = windowWidth < 900;

  // ─── Layout morph (list/detail flip pref) ───────────
  const { flip } = useListDetailFlip();
  // OFF (default): fixed 380px left column — preserves existing layout exactly.
  // ON: 2:1→1:2 flex morph driven by selection, same as AntwortenSeite.
  // isCompact path: no morph regardless of pref (single-column stacked layout).
  const MORPH_TRANSITION = 'flex 0.45s cubic-bezier(0.4,0,0.2,1)';
  const ratio = flip ? listDetailFlex(selectedUuid) : { list: 1, detail: 1 };
  const listRef = useRef<HTMLDivElement>(null);
  const closeDetail = () => {
    const row = selectedUuid
      ? listRef.current?.querySelector<HTMLElement>(`[data-uuid="${selectedUuid}"]`)
      : null;
    setSelectedUuid(null);
    (row ?? listRef.current)?.focus();
  };

  // ─── Deep-link: resolve initialTicket from loaded rows ──
  const pendingUuidRef = useRef<string | null>(initialTicket || null);
  useEffect(() => {
    if (!pendingUuidRef.current) return;
    const uuid = pendingUuidRef.current;
    const inRows = uploadRows.find(t => t.uuid === uuid);
    if (inRows) {
      pendingUuidRef.current = null;
      setSelectedUuid(uuid);
    }
    // else: rows not loaded yet; effect re-runs when rows update
  }, [uploadRows]);

  // ─── Detail loading ──────────────────────────────────
  useEffect(() => {
    if (!selectedUuid) { setSelectedTicket(null); return; }
    let cancelled = false;
    setDetailLoading(true);
    smarttix.getTicket(selectedUuid).then(detail => {
      if (!cancelled) { setSelectedTicket(detail); setDetailLoading(false); }
    });
    return () => { cancelled = true; };
  }, [selectedUuid]);

  // ─── Transient-status detail refresh ────────────────
  useTicketDetailRefresh(selectedTicket, setSelectedTicket);

  // ─── Clear stale selection (ticket left the list) ───
  // Guard: don't clear while detail pane tracks the ticket through a transient status.
  // selectedTicket (kept fresh by useTicketDetailRefresh) is the canonical liveness source.
  // Genuine orphans (non-transient status, or uuid mismatch) still clear immediately.
  useEffect(() => {
    if (!selectedUuid || hook.loading) return;
    const stillInRows = uploadRows.find(t => t.uuid === selectedUuid);
    const detailAlive = selectedTicket?.uuid === selectedUuid && TRANSIENT_STATUSES.has(selectedTicket.status);
    if (!stillInRows && !detailAlive) setSelectedUuid(null);
  }, [uploadRows, selectedUuid, hook.loading, selectedTicket]);

  // ─── Background poll (silent refresh) ───────────────
  // Keeps the uploads list live without row-clear or loading flash.
  // Stable hookRef avoids recreating the interval when hook object changes per render.
  const hookRef = useRef(hook);
  hookRef.current = hook;
  useEffect(() => {
    const id = setInterval(() => {
      hookRef.current.refresh();
    }, POLLING_INTERVAL_MS);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Save/restore per-ticket context on selection change ─
  const prevUuidRef = useRef<string | null>(null);
  const contextRef = useRef({ processContext, ocrOverrides, fieldStates, fieldOverrides });
  contextRef.current = { processContext, ocrOverrides, fieldStates, fieldOverrides };

  useEffect(() => {
    if (prevUuidRef.current) {
      ticketContextCache.set(prevUuidRef.current, { ...contextRef.current });
    }
    prevUuidRef.current = selectedUuid;
    const cached = selectedUuid ? ticketContextCache.get(selectedUuid) : null;
    if (cached) {
      setProcessContext(cached.processContext);
      setOcrOverrides(cached.ocrOverrides);
      setFieldStates(cached.fieldStates);
      setFieldOverrides(cached.fieldOverrides);
    } else {
      setProcessContext({});
      setOcrOverrides({});
      setFieldStates({});
      setFieldOverrides({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUuid]);

  // Save context on unmount (page navigation)
  useEffect(() => {
    return () => {
      if (prevUuidRef.current && contextRef.current) {
        ticketContextCache.set(prevUuidRef.current, { ...contextRef.current });
      }
    };
  }, []);

  // ─── Upload handler ──────────────────────────────────
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFilesSelected = async (e: React.DragEvent<HTMLDivElement> | React.ChangeEvent<HTMLInputElement>) => {
    let fileList: FileList | null = null;
    if ('dataTransfer' in e) {
      e.preventDefault();
      fileList = e.dataTransfer.files;
    } else {
      fileList = e.target.files;
    }
    if (!fileList || fileList.length === 0) return;

    const files = Array.from(fileList).filter(f =>
      f.name.endsWith('.eml') || f.name.endsWith('.msg')
    );
    if (files.length === 0) return;

    const ctrl = new AbortController();
    uploadAbortRef.current = ctrl;
    setUploadProgress({ current: 0, total: files.length });
    for (let i = 0; i < files.length; i++) {
      if (ctrl.signal.aborted) break;
      setUploadProgress({ current: i + 1, total: files.length });
      try {
        await smarttix.ingestTicket(files[i], { parseOnly: true, signal: ctrl.signal });
      } catch (err: any) {
        if (err?.name === 'CanceledError' || err?.name === 'AbortError' || ctrl.signal.aborted) break;
      }
      if (i < files.length - 1) await new Promise(r => setTimeout(r, UPLOAD_THROTTLE_MS));
    }
    uploadAbortRef.current = null;
    setUploadProgress(null);
    // Reset pagination so freshly-uploaded tickets appear at top.
    hook.reset();
  };

  const handleCancelUpload = () => {
    uploadAbortRef.current?.abort();
  };

  // ─── Process handler ─────────────────────────────────
  // Verarbeiten hides its row IMMEDIATELY (optimistic, fade-out — keyed off processHiddenUuids, a
  // page-local Set this handler owns), before the API call even starts, instead of leaving it
  // lingering through the whole round-trip. On success the ticket leaves the list for real
  // (hook.removeRow) — but only after waiting out whatever's left of the fade transition, since a
  // cold-start fast API resolution (see TicketManagerContext) can otherwise resolve before the CSS
  // collapse finishes, unmounting the row mid-transition and snapping the row below it up into its
  // place. On ANY failure — structured (res.success === false) or thrown — the row must fade back
  // in with a visible error state; it must never silently vanish. markProcessError-style reset at
  // the top: a retry click clears a stale badge from the previous attempt.
  const handleProcess = async (uuid: string) => {
    // Guards against double-invocation while THIS ticket is genuinely in flight (e.g. a second
    // click before the first round-trip resolves). Latent risk: tm.processingUuids is only ever
    // removed on failure (see TicketManagerContext) — it relies on processTicket (services/api.ts)
    // never throwing/hanging without resolving. If that never-throws contract ever breaks, this
    // guard would permanently block retries for the uuid; not a concern with the current
    // implementation, but worth knowing if that contract changes.
    if (tm.processingUuids.has(uuid)) return;
    const hideStartedAt = Date.now();
    setProcessHiddenUuids(prev => prev.has(uuid) ? prev : new Set(prev).add(uuid));
    setProcessErrorUuids(prev => {
      if (!prev.has(uuid)) return prev;
      const next = new Set(prev);
      next.delete(uuid);
      return next;
    });
    const markProcessError = () => {
      setProcessErrorUuids(prev => new Set(prev).add(uuid));
      // Failure reappearance: stop hiding locally too — hasProcessError already forces the row
      // visible regardless, but clearing this keeps processHiddenUuids from accumulating stale
      // entries across retries.
      setProcessHiddenUuids(prev => {
        if (!prev.has(uuid)) return prev;
        const next = new Set(prev);
        next.delete(uuid);
        return next;
      });
    };
    try {
      const ctx: ProcessContext = { ...processContext };
      const overrides = Object.entries(ocrOverrides)
        .filter(([, v]) => v !== 'auto')
        .map(([filename, decision]) => ({ filename, decision }));
      if (overrides.length > 0) ctx.ocrOverrides = overrides;
      const verifications = Object.entries(fieldStates)
        .filter(([, v]) => v !== 'unverified')
        .map(([field, status]) => ({
          field, status,
          ...(status === 'incorrect' && fieldOverrides[field] ? { override: fieldOverrides[field] } : {}),
        }));
      if (verifications.length > 0) ctx.fieldVerifications = verifications;

      const res = await tm.processTicket(uuid, Object.keys(ctx).length > 0 ? ctx : undefined);
      if (res.success) {
        ticketContextCache.delete(uuid);
        // Row is still visually hidden (processHiddenUuids stays true on success) — but if the API
        // resolved faster than PROCESS_FADE_MS, wait out the remainder before splicing it for real.
        const remainingFadeMs = PROCESS_FADE_MS - (Date.now() - hideStartedAt);
        if (remainingFadeMs > 0) await new Promise(r => setTimeout(r, remainingFadeMs));
        hook.removeRow(uuid);
        // processHiddenUuids is deliberately NOT cleared here — see the garbage-collection effect
        // near its declaration. A background refresh() can still re-insert this uuid into
        // uploadRows if the server-side status hasn't left the uploads bucket yet; the hidden flag
        // must survive that until a fetch genuinely stops including the ticket.
        if (selectedUuid === uuid) setSelectedUuid(null);
        if (onNavigate && uiWarps.processToAntworten) onNavigate('responses', { ticket: uuid });
      } else {
        markProcessError();
      }
    } catch {
      markProcessError();
    }
  };

  const handleDelete = async (uuid: string) => {
    if (tm.deletingUuids.has(uuid)) return;
    const res = await tm.deleteTicket(uuid);
    if (res.success) {
      hook.removeRow(uuid);
      if (selectedUuid === uuid) setSelectedUuid(null);
      ticketContextCache.delete(uuid);
    }
  };

  // ─── Auto-button ─────────────────────────────────────
  // Operates on loaded (paginated) rows — matches AntwortenSeite convention.
  const autoTargetUuids = sel.selected.size > 0
    ? uploadRows.filter(t => sel.selected.has(t.uuid)).map(t => t.uuid)
    : uploadRows.map(t => t.uuid);
  const autoTargetCount = autoTargetUuids.length;
  const autoEtaMinutes = Math.max(1, Math.ceil(autoTargetCount * 12 / 60));
  const autoHasAction = autoTargetCount > 0;
  const autoIcon = autoHasAction
    ? <LightningIcon size={16} color={theme.colors.white} />
    : <AutoIcon size={16} color={theme.colors.white} />;
  const autoNoActionMessage = sel.mode && sel.selected.size === 0
    ? 'Keine Auswahl zum Verarbeiten.'
    : 'Bitte zuerst E-Mails hochladen.';

  const handleAutoClick = () => {
    if (autoSubmitting) return;
    if (!autoHasAction) { setInfoToast(autoNoActionMessage); return; }
    setShowAutoModal(true);
  };

  const handleAutoConfirm = async () => {
    if (autoTargetCount === 0 || autoSubmitting) return;
    setAutoSubmitting(true);
    try {
      const uuids = autoTargetUuids.slice();
      const res = await smarttix.bulkProcess(uuids);
      sel.exitMode();
      setShowAutoModal(false);
      setSuccessToast({ count: res.enqueued });
      hook.reset();
    } finally {
      setAutoSubmitting(false);
    }
  };

  // ─── Counts ──────────────────────────────────────────
  const uploadsCount = hook.counts.uploads;

  return (
    <div style={{ fontFamily: theme.font.family }}>
      {/* Title row */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: theme.spacing.md,
        paddingBottom: theme.spacing.md,
        borderBottom: `1px solid ${theme.colors.borderLight}`,
        marginBottom: theme.spacing.lg,
      }}>
        <h1 style={{
          fontSize: 22, fontWeight: 600, color: theme.colors.navy,
          margin: 0, flex: 1,
        }}>
          {hook.loading && uploadRows.length === 0
            ? 'Uploads'
            : uploadsCount > 0
              ? `${uploadsCount} Ticket${uploadsCount !== 1 ? 's' : ''} eingereicht`
              : 'Uploads'}
        </h1>
        <SelectionStrip count={sel.selected.size} onClear={sel.clear} />
        {uploadRows.length > 0 && (
          <button
            onClick={() => sel.setPurpose('archive')}
            aria-label={sel.mode ? 'Auswahlmodus beenden' : 'Auswahlmodus starten'}
            title={sel.mode ? 'Auswahlmodus beenden' : 'Auswahlmodus starten'}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 40, height: 40, padding: 0,
              background: sel.mode ? theme.colors.navy : 'transparent',
              border: `1px solid ${sel.mode ? theme.colors.navy : theme.colors.borderLight}`,
              borderRadius: theme.borderRadius.sm,
              cursor: 'pointer',
              color: sel.mode ? theme.colors.white : theme.colors.navyMid,
            }}
          >
            <SelectionToggleIcon size={14} color={sel.mode ? theme.colors.white : theme.colors.navyMid} active={sel.mode} />
          </button>
        )}
        <AutoButton
          icon={autoIcon}
          onClick={handleAutoClick}
          title={autoHasAction ? `Verarbeiten (${autoTargetCount})` : autoNoActionMessage}
        />
      </div>

      {/* 2-column layout */}
      <div style={{
        display: 'flex',
        gap: theme.spacing.lg,
        flexDirection: isCompact ? 'column' : 'row',
        ...(isCompact ? {} : { height: 'calc(100vh - 200px)', minHeight: 400 }),
      }}>

        {/* ═══ Left: uploads list ═══ */}
        <BentoCard
          title="Uploads"
          style={{
            display: 'flex', flexDirection: 'column', minWidth: 0,
            // Why: when flip is OFF, preserve fixed 380px width (existing behaviour).
            // When flip is ON, morph via flex ratio so the drop-zone + list narrows
            // when a ticket is selected (mirrors Antworten 2:1→1:2 pattern).
            ...(isCompact
              ? { width: '100%', maxHeight: 500 }
              : flip
                ? { flex: ratio.list, transition: MORPH_TRANSITION }
                : { width: 380, flexShrink: 0 }),
          }}
          contentStyle={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', paddingBottom: 0 }}
        >
          {/* Drop zone — keyboard-operable per WCAG 2.1.1 */}
          <div
            role="button"
            tabIndex={0}
            aria-label="E-Mail-Datei zum Hochladen auswählen oder per Drag & Drop ablegen"
            onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={e => { setIsDragOver(false); handleFilesSelected(e); }}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            style={{
              border: `2px dashed ${isDragOver ? theme.colors.navy : theme.colors.borderLight}`,
              borderRadius: theme.borderRadius.md,
              padding: theme.spacing.md,
              marginBottom: theme.spacing.sm,
              display: 'flex', alignItems: 'center', gap: theme.spacing.md,
              cursor: 'pointer',
              backgroundColor: isDragOver ? theme.colors.bgLight : 'transparent',
              transition: 'border-color 0.2s ease, background-color 0.2s ease',
              minHeight: 52,
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".eml,.msg"
              multiple
              style={{ display: 'none' }}
              onChange={handleFilesSelected}
            />
            <svg aria-hidden="true" focusable="false" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={theme.colors.navy} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="M22 4L12 13L2 4" />
            </svg>
            <div style={{ flex: 1, minWidth: 0 }}>
              {uploadProgress ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                  <div style={{ flex: 1, height: 4, backgroundColor: theme.colors.bgLight, borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', width: `${(uploadProgress.current / uploadProgress.total) * 100}%`,
                      backgroundColor: theme.colors.navy, transition: 'width 0.3s ease',
                    }} />
                  </div>
                  <span style={{ fontSize: 12, color: theme.colors.navyMid, whiteSpace: 'nowrap' }}>
                    {uploadProgress.current}/{uploadProgress.total}
                  </span>
                  {/* WCAG 2.2.1 / DGUV Ch.9 — cancellable long op */}
                  <button
                    type="button"
                    onClick={e => { e.stopPropagation(); handleCancelUpload(); }}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); } }}
                    style={{
                      fontSize: 12, fontWeight: 500, fontFamily: theme.font.family,
                      padding: '4px 10px', borderRadius: theme.borderRadius.sm,
                      border: `1px solid ${theme.colors.borderLight}`,
                      background: theme.colors.white, color: theme.colors.navyMid,
                      cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    Abbrechen
                  </button>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 13, fontWeight: 500, color: theme.colors.navy }}>
                    E-Mails per Drag & Drop oder Klick hochladen
                  </div>
                  <div style={{ fontSize: 11, color: theme.colors.textSecondary }}>
                    .eml / .msg
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Sort toggle + column header */}
          <div style={{
            display: 'flex', alignItems: 'center',
            padding: `${theme.spacing.xs}px ${theme.spacing.md}px ${theme.spacing.xs}px ${4 + theme.spacing.sm}px`,
            fontSize: 11, fontWeight: 600, color: theme.colors.textSecondary,
            textTransform: 'uppercase', letterSpacing: 0.5,
            borderBottom: `1px solid ${theme.colors.borderLight}`,
            userSelect: 'none', gap: 4,
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>Betreff</div>
            <select
              id="uploads-sort"
              name="uploads-sort"
              value={sort}
              onChange={e => setSort(e.target.value as ActiveSort)}
              aria-label="Sortierspalte"
              style={{
                flexShrink: 0, fontSize: 11, fontWeight: 600,
                color: theme.colors.navy, background: theme.colors.white,
                border: `1px solid ${theme.colors.borderLight}`,
                borderRadius: theme.borderRadius.sm,
                padding: '2px 4px', cursor: 'pointer',
                fontFamily: theme.font.family,
                outline: 'none', textTransform: 'uppercase', letterSpacing: 0.3,
              }}
            >
              <option value="updated">Aktualisiert</option>
              <option value="uploaded">Hochgeladen</option>
              <option value="received">E-Mail-Eingang</option>
              <option value="rejections">Neuanalysen</option>
            </select>
            <button
              onClick={() => setDir(d => d === 'desc' ? 'asc' : 'desc')}
              aria-label={dir === 'desc' ? 'Absteigende Reihenfolge — klicken für aufsteigend' : 'Aufsteigende Reihenfolge — klicken für absteigend'}
              title={dir === 'desc' ? 'Absteigende Reihenfolge' : 'Aufsteigende Reihenfolge'}
              style={{
                flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 26, height: 26, padding: 0,
                color: theme.colors.navy, background: 'none',
                border: `1px solid ${theme.colors.borderLight}`,
                borderRadius: theme.borderRadius.sm,
                cursor: 'pointer',
              }}
            >
              <SortDirectionIcon size={13} color={theme.colors.navy} dir={dir} />
            </button>
          </div>

          {/* Scrollable row list + sentinel */}
          <div ref={listRef} tabIndex={-1} className="stx-no-scrollbar" style={{ flex: 1, overflowY: 'auto', margin: `0 -${theme.spacing.md}px`, outline: 'none' }}>
            {hook.loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: theme.colors.textSecondary, fontSize: 13 }}>
                Lade Tickets...
              </div>
            ) : hook.error ? (
              <div style={{ padding: 40, textAlign: 'center', color: theme.colors.error, fontSize: 13 }}>
                {hook.error}
                <div style={{ marginTop: theme.spacing.sm }}>
                  <button
                    onClick={() => hook.reset()}
                    style={{
                      padding: '6px 14px', fontSize: 13, fontWeight: 500,
                      fontFamily: theme.font.family,
                      background: 'transparent', color: theme.colors.navy,
                      border: `1px solid ${theme.colors.borderLight}`,
                      borderRadius: theme.borderRadius.sm, cursor: 'pointer',
                    }}
                  >Erneut versuchen</button>
                </div>
              </div>
            ) : uploadRows.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: theme.colors.textSecondary, fontSize: 14 }}>
                Keine eingereichten Tickets
              </div>
            ) : (
              uploadRows.map(t => {
                const isSelected = selectedUuid === t.uuid;
                const isDeleting = tm.deletingUuids.has(t.uuid);
                const isPicked = sel.selected.has(t.uuid);
                const showUnreadDot = !isRead(t.uuid);
                // Optimistic Verarbeiten: isProcessFading collapses the row (opacity + max-height,
                // both transitioned) the instant the click happens, well before the API resolves —
                // keyed off processHiddenUuids, a page-local Set written only by this page's own
                // handleProcess (see handleProcess's post-resolve fade-out wait). Deliberately NOT
                // keyed off tm.processingUuids: that's an app-wide in-flight signal written by other
                // pages too (Dashboard, Testgenerator) and never cleared on success, so a ticket
                // processed elsewhere and merely passing through this list — no click of its own —
                // would otherwise render permanently hidden here with no path back.
                // hasProcessError both renders the error line AND forces the row back regardless of
                // processHiddenUuids — needed for the (defensive-only; the real API never throws)
                // thrown-failure path.
                const hasProcessError = processErrorUuids.has(t.uuid);
                const isProcessFading = processHiddenUuids.has(t.uuid) && !hasProcessError;
                return (
                  <div
                    key={t.uuid}
                    data-uuid={t.uuid}
                    tabIndex={0}
                    aria-label={`Ticket ${t.subject ?? t.uuid}`}
                    onClick={() => {
                      if (sel.mode) { sel.toggle(t.uuid); return; }
                      markRead(t.uuid);
                      setSelectedUuid(isSelected ? null : t.uuid);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (sel.mode) { sel.toggle(t.uuid); return; }
                        markRead(t.uuid);
                        setSelectedUuid(isSelected ? null : t.uuid);
                      }
                    }}
                    style={{
                      display: 'flex',
                      cursor: 'pointer',
                      opacity: isDeleting || isProcessFading ? 0 : 1,
                      // maxHeight (fixed numeric bound, not 'none' — a transition needs two
                      // numbers to animate between) collapses the row smoothly on optimistic hide
                      // instead of an abrupt array-splice jump; by the time the API round-trip
                      // actually resolves the row is already invisible+0-height, so removing it
                      // for real (success) or restoring it (failure) causes no further shift.
                      maxHeight: isProcessFading ? 0 : 220,
                      overflow: isProcessFading ? 'hidden' : 'visible',
                      pointerEvents: isProcessFading ? 'none' : undefined,
                      transition: `opacity ${PROCESS_FADE_MS}ms ease, max-height ${PROCESS_FADE_MS}ms ease, background-color 0.15s ease`,
                      backgroundColor: isPicked ? theme.colors.bgLight : (isSelected ? theme.colors.bgLight : 'transparent'),
                      borderLeft: isPicked ? `4px solid ${theme.colors.navy}` : '4px solid transparent',
                      borderBottom: `1px solid ${theme.colors.borderLight}`,
                      animation: 'stx-fadeIn 0.3s ease',
                    }}
                  >
                    {/* Category strip */}
                    <div style={{
                      width: 32, flexShrink: 0,
                      backgroundColor: t.primaryAxis ? cm.color(t.primaryAxis) : theme.statusStrip.unclassified,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }} />
                    <div style={{
                      flex: 1, minWidth: 0,
                      // Why: right-padding pre-allocates dot space to prevent layout shift
                      padding: `6px ${theme.spacing.sm + 2}px 6px 12px`,
                      position: 'relative',
                    }}>
                      {/* Unread dot — DB-backed via useReadState (#6) */}
                      {showUnreadDot && (
                        <div
                          data-testid="unread-dot"
                          aria-hidden="true"
                          style={{
                            position: 'absolute', top: theme.spacing.sm, right: theme.spacing.sm,
                            width: 8, height: 8, borderRadius: '50%',
                            backgroundColor: theme.colors.navy,
                            animation: 'stx-fadeIn 0.2s ease',
                          }}
                        />
                      )}
                      {/* Row 1: subject + paperclip */}
                      <div style={{
                        fontSize: 13, fontWeight: 500, color: theme.colors.navy,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        // Pre-allocated space for dot (prevents subject text sliding right/left)
                        paddingRight: 16,
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.subject || '(Kein Betreff)'}</span>
                        {/* Slot reserved only for rows that could get a glyph (mayShowAttachmentGlyph)
                            — reserving on a no-attachment row makes settle collapse the width and its
                            gap, the shift the slot exists to prevent. Slot size tracks the
                            PaperclipIcon size painted here (12), not TicketRowMeta's 13. */}
                        {mayShowAttachmentGlyph(t) && (
                          cm.configLoading ? <AttachmentGlyphSlot size={12} /> : shouldShowAttachmentGlyph(t, attachmentsCountInline ?? true) && (
                            <span title="Anhang" style={{ flexShrink: 0, display: 'flex' }}><PaperclipIcon size={12} /></span>
                          )
                        )}
                      </div>
                      {/* Row 2: uploader */}
                      <div style={{ fontSize: 11, color: theme.colors.textSecondary, marginTop: 3 }}>
                        {t.uploadedByName || t.uploadedByEmail || '—'}
                      </div>
                      {/* Row 3: date + reanalysis count + Kopiert + trash */}
                      <div style={{ display: 'flex', alignItems: 'center', marginTop: 3, gap: 4 }}>
                        <span style={{ fontSize: 11, color: theme.colors.navyMuted, flex: 1 }}>
                          {formatRelativeTime(sort === 'received' ? t.receivedAt : t.createdAt ?? t.receivedAt)}
                        </span>
                        {t.reanalysisCount != null && t.reanalysisCount > 0 && (
                          <span data-testid="reanalysis-count-badge" title={`${t.reanalysisCount} Neuanalyse${t.reanalysisCount === 1 ? '' : 'n'}`} style={{
                            fontSize: 10, fontWeight: 600,
                            color: theme.colors.navyMid, backgroundColor: theme.colors.bgLight,
                            border: `1px solid ${theme.colors.borderLight}`,
                            borderRadius: 8, padding: '1px 6px',
                            display: 'inline-flex', alignItems: 'center', gap: 2,
                          }}>
                            <ReanalysisIcon size={11} color={theme.colors.navyMid} />{t.reanalysisCount}
                          </span>
                        )}
                        {t.copyApprovedAt && (
                          <span data-testid="kopiert-badge" title="Antwort kopiert, nicht förmlich genehmigt" style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            fontSize: 10, fontWeight: 500,
                            color: theme.colors.navyMid, backgroundColor: theme.colors.bgLight,
                            border: `1px solid ${theme.colors.borderLight}`,
                            borderRadius: 8, padding: '1px 6px',
                            whiteSpace: 'nowrap', flexShrink: 0,
                          }}>
                            <KopiertIcon size={10} color={theme.colors.navyMid} />
                            Kopiert
                          </span>
                        )}
                        {t.edited === true && (
                          <span data-testid="bearbeitet-badge" style={{
                            fontSize: 10, fontWeight: 500, padding: '1px 6px', borderRadius: 8,
                            color: theme.colors.navyMid, backgroundColor: theme.colors.bgLight,
                            border: `1px solid ${theme.colors.borderLight}`,
                            whiteSpace: 'nowrap', flexShrink: 0,
                          }}>bearbeitet</span>
                        )}
                        <button
                          onClick={e => { e.stopPropagation(); handleDelete(t.uuid); }}
                          disabled={isDeleting}
                          title="Löschen"
                          style={{
                            padding: '3px 4px', lineHeight: 1, display: 'flex', alignItems: 'center',
                            background: 'none',
                            border: `1px solid ${theme.colors.borderLight}`,
                            borderRadius: theme.borderRadius.sm,
                            cursor: isDeleting ? 'default' : 'pointer',
                            color: theme.colors.textSecondary,
                            opacity: isDeleting ? 0.3 : 0.7,
                            transition: 'opacity 0.15s ease',
                          }}
                          onMouseEnter={e => { if (!isDeleting) (e.currentTarget).style.opacity = '1'; }}
                          onMouseLeave={e => { (e.currentTarget).style.opacity = isDeleting ? '0.3' : '0.7'; }}
                        >
                          <TrashIcon size={13} />
                        </button>
                      </div>
                      {/* Verarbeiten failed after the optimistic hide reversed — fades in with the
                          row; a retry click (Verarbeiten again from the Konfiguration panel)
                          clears it via handleProcess's reappearWithError reset. */}
                      {hasProcessError && (
                        <div data-testid="process-error" style={{
                          fontSize: 11, color: theme.colors.error, marginTop: 3,
                          animation: 'stx-fadeIn 0.25s ease',
                        }}>
                          Verarbeitung fehlgeschlagen — bitte erneut versuchen
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}

            {/* Infinite-scroll sentinel */}
            {!hook.loading && !hook.error && uploadRows.length > 0 && (
              <div ref={hook.sentinelRef} aria-hidden="true" style={{ height: 1 }} />
            )}
            {hook.loadingMore && (
              <div style={{ padding: 16, textAlign: 'center', color: theme.colors.textSecondary, fontSize: 12 }}>
                Lade weitere…
              </div>
            )}
          </div>
        </BentoCard>

        {/* ═══ Right: context panel ═══ */}
        <BentoCard
          title="Konfiguration"
          headerAction={selectedUuid ? (
            <button
              onClick={closeDetail}
              aria-label="Auswahl aufheben"
              title="Auswahl aufheben"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 24, height: 24, padding: 0, cursor: 'pointer',
                background: 'transparent', border: 'none', borderRadius: theme.borderRadius.sm,
                color: theme.colors.navyMuted, outline: 'none',
                animation: 'stx-fadeIn 0.2s ease',
              }}
            >
              <CrossIcon size={16} />
            </button>
          ) : undefined}
          style={{
            display: 'flex', flexDirection: 'column', minWidth: 0,
            ...(isCompact
              ? { minHeight: 300 }
              : flip
                ? { flex: ratio.detail, transition: MORPH_TRANSITION }
                : { flex: 1 }),
          }}
          contentStyle={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0, minHeight: 0 }}
        >
          {!selectedUuid ? (
            <div style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: theme.colors.textSecondary, fontSize: 15,
            }}>
              Ticket auswählen
            </div>
          ) : detailLoading ? (
            <div style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: theme.colors.textSecondary, fontSize: 14,
            }}>
              Laden...
            </div>
          ) : selectedTicket ? (
            <ContextPanel
              ticket={selectedTicket}
              processContext={processContext}
              setProcessContext={setProcessContext}
              ocrOverrides={ocrOverrides}
              setOcrOverrides={setOcrOverrides}
              fieldStates={fieldStates}
              setFieldStates={setFieldStates}
              fieldOverrides={fieldOverrides}
              setFieldOverrides={setFieldOverrides}
              onProcess={() => handleProcess(selectedTicket.uuid)}
              onDelete={() => handleDelete(selectedTicket.uuid)}
              isProcessing={tm.processingUuids.has(selectedTicket.uuid)}
              isDeleting={tm.deletingUuids.has(selectedTicket.uuid)}
              attachmentsCountInline={attachmentsCountInline}
            />
          ) : (
            <div style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: theme.colors.textSecondary, fontSize: 14,
            }}>
              Ticket nicht gefunden
            </div>
          )}
        </BentoCard>
      </div>

      <RueckgaengigToast
        visible={!!infoToast}
        message={infoToast ?? ''}
        onDismiss={() => setInfoToast(null)}
      />

      <RueckgaengigToast
        visible={!!successToast}
        message={successToast ? (
          <><CountUpNumber target={successToast.count} /> Ticket{successToast.count !== 1 ? 's' : ''} in Verarbeitung</>
        ) : ''}
        onDismiss={() => setSuccessToast(null)}
      />

      {/* Auto-Verarbeiten confirm modal */}
      {showAutoModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,13,102,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 200,
          animation: 'stx-fadeIn 0.15s ease',
        }} onClick={() => !autoSubmitting && setShowAutoModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            backgroundColor: theme.colors.white, borderRadius: theme.borderRadius.md,
            padding: `${theme.spacing.lg}px ${theme.spacing.lg + theme.spacing.sm}px`,
            minWidth: 320, maxWidth: 420,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            fontFamily: theme.font.family,
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: theme.spacing.sm,
              marginBottom: theme.spacing.sm,
            }}>
              <h2 style={{
                fontSize: 16, fontWeight: 600, color: theme.colors.navy,
                margin: 0, flex: 1,
              }}>Verarbeitung starten?</h2>
              <button
                onClick={() => !autoSubmitting && setShowAutoModal(false)}
                aria-label="Schließen"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                  display: 'inline-flex', alignItems: 'center',
                }}
              >
                <CrossIcon size={14} color={theme.colors.navyMuted} />
              </button>
            </div>
            <p style={{
              fontSize: 13, color: theme.colors.navyMid,
              margin: `0 0 ${theme.spacing.sm}px 0`, fontWeight: 600,
            }}>
              {autoTargetCount} Ticket{autoTargetCount !== 1 ? 's' : ''} · ca. {autoEtaMinutes} Min.
            </p>
            <p style={{
              fontSize: 12, color: theme.colors.navyMid,
              margin: `0 0 ${theme.spacing.lg}px 0`, lineHeight: 1.5,
            }}>
              Die KI klassifiziert die Tickets und erstellt Antwortvorschläge.
              Sie können einzelne Vorschläge anschließend auf der Antwortenseite
              prüfen, genehmigen oder ablehnen.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm }}>
              <button
                onClick={() => setShowAutoModal(false)}
                disabled={autoSubmitting}
                style={{
                  padding: '6px 14px', fontSize: 13, fontWeight: 500,
                  fontFamily: theme.font.family,
                  background: 'transparent', color: theme.colors.navyMid,
                  border: `1px solid ${theme.colors.borderLight}`,
                  borderRadius: theme.borderRadius.sm,
                  cursor: autoSubmitting ? 'not-allowed' : 'pointer',
                }}
              >Abbrechen</button>
              <button
                onClick={handleAutoConfirm}
                disabled={autoSubmitting || autoTargetCount === 0}
                style={{
                  padding: '6px 14px', fontSize: 13, fontWeight: 600,
                  fontFamily: theme.font.family,
                  background: theme.colors.navy, color: theme.colors.white,
                  border: 'none', borderRadius: theme.borderRadius.sm,
                  cursor: autoSubmitting ? 'not-allowed' : 'pointer',
                  opacity: autoSubmitting ? 0.6 : 1,
                }}
              >{autoSubmitting ? 'Wird gestartet…' : 'Verarbeiten'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ═════════════════════════════════════════════════════════
// ContextPanel — right column detail + controls
// ═════════════════════════════════════════════════════════
interface ContextPanelProps {
  ticket: TicketDetail;
  processContext: ProcessContext;
  setProcessContext: React.Dispatch<React.SetStateAction<ProcessContext>>;
  ocrOverrides: Record<string, 'force' | 'auto' | 'skip'>;
  setOcrOverrides: React.Dispatch<React.SetStateAction<Record<string, 'force' | 'auto' | 'skip'>>>;
  fieldStates: Record<string, 'verified' | 'unverified' | 'incorrect'>;
  setFieldStates: React.Dispatch<React.SetStateAction<Record<string, 'verified' | 'unverified' | 'incorrect'>>>;
  fieldOverrides: Record<string, string>;
  setFieldOverrides: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  onProcess: () => void;
  onDelete: () => void;
  isProcessing: boolean;
  isDeleting: boolean;
  // Tenant setting (operator_config UI_ATTACHMENTS_COUNT_INLINE). ABSENT ⇒ true — reproduces the
  // behaviour that shipped before this rule existed, matching TicketDetail's own default.
  attachmentsCountInline?: boolean;
}

const ContextPanel: React.FC<ContextPanelProps> = ({
  ticket, processContext, setProcessContext,
  ocrOverrides, setOcrOverrides,
  fieldStates, setFieldStates,
  fieldOverrides, setFieldOverrides,
  onProcess, onDelete, isProcessing, isDeleting,
  attachmentsCountInline = true,
}) => {
  const maintenance = useMaintenanceMode();
  const cm = useCategoryMeta();
  const attachments: AttachmentInfo[] = ticket.attachments ?? [];
  // UploadsSeite's ticket prop is TicketDetail (attachments only, no ocrDecisions yet — this is
  // pre-processing, before any OCR run has happened) — exactly why the ingest isInline stamp
  // exists: isInlineAttachment resolves the inline verdict from the stamp alone when no decision
  // exists yet, same fallback resolveAttachmentOcrRows always used.
  const anhaengeRows = resolveAttachmentOcrRows(attachments, undefined);
  const { documents: anhaengeDocuments, inline: anhaengeInline } = partitionAttachmentRows(anhaengeRows);
  // ON renders the untouched resolved row array (today's join order, byte-identical) — never
  // [...documents, ...inline], which would silently reorder a row whose OCR-decision fallback
  // (isInlineAttachment) disagrees with its position in the raw list. OFF is documents-only.
  const anhaengeVisibleRows = attachmentsCountInline ? anhaengeRows : anhaengeDocuments;
  const anhaengeCount = countAttachments(anhaengeRows, attachmentsCountInline);
  // Shared row renderer — same JSX regardless of which bucket (documents/inline) or posture
  // (ON/OFF) a row is drawn from, so the control itself can never differ across the split.
  const renderAnhangRow = (row: AttachmentOcrRow) => (
    <div key={row.name} style={{
      display: 'flex', alignItems: 'center', gap: theme.spacing.sm,
      padding: `${theme.spacing.xs}px 0`,
    }}>
      <span style={{
        flex: 1, fontSize: 12, color: theme.colors.navyMid,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        minWidth: 0,
      }}>
        {row.name}
      </span>
      <TriStateControl
        options={[...OCR_OPTIONS]}
        selected={ocrOverrides[row.name] || 'auto'}
        onChange={v => setOcrOverrides(prev => ({ ...prev, [row.name]: v as 'force' | 'auto' | 'skip' }))}
      />
    </div>
  );
  const extract = ticket.ingestionExtract;

  const checklistRows = [
    { key: 'checklist.absender', label: 'Absender', value: ticket.customerEmail, alwaysPresent: true },
    { key: 'checklist.name', label: 'Name', value: extract?.customerName ?? null },
    { key: 'checklist.plz', label: 'PLZ', value: extract?.plz ?? ticket.plz ?? null },
    { key: 'checklist.zaehler', label: extract?.meterType ?? 'Zähler', value: extract?.meterId ?? null },
  ];

  const [bodyExpanded, setBodyExpanded] = useState(true);

  // ─── PII click-to-reveal (pre-process only) ──────────────────────
  const [revealedPii, setRevealedPii] = useState<Set<string>>(() => new Set());
  const prevUuidRef = useRef<string | null>(null);
  useEffect(() => {
    if (ticket.uuid !== prevUuidRef.current) {
      prevUuidRef.current = ticket.uuid;
      setRevealedPii(new Set());
    }
  }, [ticket.uuid]);

  const personalData = (ticket.personalData ?? null) as Record<string, any> | null;
  const canReveal = !!personalData && (ticket.status === 'submitted' || ticket.status === 'ingested');
  const revealCtx: RevealContext | undefined = canReveal
    ? { personalData: personalData!, revealed: revealedPii }
    : undefined;

  const onBodyPreviewClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!canReveal) return;
    let el = e.target as HTMLElement | null;
    while (el && el !== e.currentTarget && !el.dataset?.piiType) {
      el = el.parentElement;
    }
    if (!el || el === e.currentTarget) return;
    const type = el.dataset.piiType!;
    const idxStr = el.dataset.piiIndex!;
    const idx = Number(idxStr);
    if (!Number.isFinite(idx)) return;
    const key = `${type}:${idx}`;
    setRevealedPii(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
        smarttix.logPiiReveal(ticket.uuid, type, idx);
      }
      return next;
    });
  };

  const FIELD_OPTIONS = [
    { value: 'verified', label: '✓' },
    { value: 'unverified', label: '?' },
    { value: 'incorrect', label: '✗' },
  ] as const;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      animation: 'stx-fadeIn 0.3s ease',
      flex: 1, minHeight: 0,
    }}>
      {/* ─── PINNED header ─────── */}
      <div style={{
        padding: `${theme.spacing.lg}px ${theme.spacing.lg}px 0 ${theme.spacing.lg}px`,
        borderBottom: `1px solid ${theme.colors.borderLight}`,
        paddingBottom: theme.spacing.md,
      }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: theme.colors.navy, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>{ticket.subject || '(Kein Betreff)'}</span>
          {/* Keyed to anhaengeCount — the SAME counting rule the Anhänge panel below this header
              renders. Counting the raw array here instead let a flag-OFF tenant see a paperclip in
              this header while the panel directly beneath it said "Anhänge (0)". Slot/skeleton
              posture matches the list rows: reserve only when attachments exist, never paint a
              glyph that settle would take away. */}
          {attachments.length > 0 && (
            cm.configLoading ? <AttachmentGlyphSlot size={14} /> : anhaengeCount > 0 && (
              <span title="Anhang" style={{ display: 'flex' }}><PaperclipIcon size={14} /></span>
            )
          )}
        </h2>
        <div style={{ fontSize: 13, color: theme.colors.textSecondary, marginTop: theme.spacing.xs, display: 'flex', flexWrap: 'wrap', gap: theme.spacing.sm }}>
          <span>{ticket.customerEmail}</span>
          <span style={{ color: theme.colors.borderLight }}>|</span>
          <span>{new Date(ticket.receivedAt).toLocaleDateString('de-DE', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
          })}</span>
        </div>
      </div>

      {/* ─── SCROLLABLE sections ─────── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: `0 ${theme.spacing.lg}px`, paddingTop: theme.spacing.lg }}>
        {ticket.emailBodyPreview && (
          <PanelSection
            title="E-Mail-Inhalt"
            collapsible
            expanded={bodyExpanded}
            onToggle={() => setBodyExpanded(p => !p)}
          >
            {isHtml(ticket.emailBodyPreview) ? (
              <div
                onClick={onBodyPreviewClick}
                style={{
                  padding: theme.spacing.md, backgroundColor: theme.colors.bgPage,
                  borderRadius: theme.borderRadius.md, border: `1px solid ${theme.colors.borderLight}`,
                  fontSize: 13, lineHeight: 1.6, color: theme.colors.navyMid,
                }}
                dangerouslySetInnerHTML={{
                  __html: styleRedactionTokens(DOMPurify.sanitize(ticket.emailBodyPreview, {
                    FORBID_TAGS: ['style', 'link', 'meta', 'head', 'title', 'script', 'img'],
                    FORBID_ATTR: ['style', 'class'],
                    ADD_DATA_URI_TAGS: [],
                    ADD_ATTR: ['data-pii-type', 'data-pii-index'],
                  }), revealCtx),
                }}
              />
            ) : (
              <div
                onClick={onBodyPreviewClick}
                style={{
                  padding: theme.spacing.md, backgroundColor: theme.colors.bgPage,
                  borderRadius: theme.borderRadius.md, border: `1px solid ${theme.colors.borderLight}`,
                  whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.6, color: theme.colors.navyMid,
                  wordBreak: 'break-word',
                }}
                dangerouslySetInnerHTML={{
                  __html: styleRedactionTokens(escapeHtmlText(ticket.emailBodyPreview), revealCtx),
                }}
              />
            )}
            {canReveal && (
              <div style={{ marginTop: 6, fontSize: 11, color: theme.colors.textSecondary, fontStyle: 'italic' }}>
                Pillen anklicken, um die ursprünglichen Daten kurzzeitig einzusehen — nur vor der Verarbeitung verfügbar.
              </div>
            )}
          </PanelSection>
        )}

        <PanelSection title="Kundendaten">
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
            {checklistRows.map(row => {
              const state = fieldStates[row.key] || 'unverified';
              const hasValue = row.value != null && row.value !== '';
              return (
                <div key={row.key}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: theme.spacing.sm,
                    minHeight: 28,
                  }}>
                    <span style={{ fontSize: 11, color: theme.colors.textSecondary, width: 90, flexShrink: 0 }}>
                      {row.label}
                    </span>
                    <span style={{
                      flex: 1, fontSize: 12, minWidth: 0,
                      color: hasValue
                        ? (state === 'incorrect' ? theme.colors.error : theme.colors.navyMid)
                        : theme.colors.textSecondary,
                      fontStyle: hasValue ? 'normal' : 'italic',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {hasValue ? row.value : 'nicht erkannt'}
                    </span>
                    {hasValue && (
                      <TriStateControl
                        options={[...FIELD_OPTIONS]}
                        selected={state}
                        onChange={v => setFieldStates(prev => ({ ...prev, [row.key]: v as 'verified' | 'unverified' | 'incorrect' }))}
                      />
                    )}
                  </div>
                  {(state === 'incorrect' || !hasValue) && (
                    <div style={{ paddingLeft: 90 + theme.spacing.sm, marginTop: theme.spacing.xs }}>
                      <input
                        id={`field-override-${row.key}`}
                        name={`field-override-${row.key}`}
                        type="text"
                        value={fieldOverrides[row.key] || ''}
                        onChange={e => setFieldOverrides(prev => ({ ...prev, [row.key]: e.target.value }))}
                        placeholder={hasValue ? row.value! : `${row.label} eingeben...`}
                        style={{
                          width: '100%', boxSizing: 'border-box',
                          padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
                          border: `1px solid ${theme.colors.borderLight}`,
                          borderRadius: theme.borderRadius.sm,
                          fontSize: 12, fontFamily: theme.font.family,
                          color: theme.colors.navy, outline: 'none',
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </PanelSection>

        <PanelSection title={`${cm.secondaryAxisNoun} vorgeben`}>
          <select
            id="uploads-anliegen"
            name="uploads-anliegen"
            value=""
            onChange={e => {
              const code = e.target.value;
              if (!code) return;
              setProcessContext(p => {
                const current = p.forceSecondaryAxis ?? [];
                if (current.includes(code)) return p;
                return { ...p, forceSecondaryAxis: [...current, code] };
              });
              e.target.value = '';
            }}
            style={{
              width: '100%', padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
              fontSize: 13, fontFamily: theme.font.family,
              border: `1px solid ${(processContext.forceSecondaryAxis ?? []).length > 0 ? theme.colors.navy : theme.colors.borderLight}`,
              borderRadius: theme.borderRadius.sm,
              color: (processContext.forceSecondaryAxis ?? []).length > 0 ? theme.colors.navy : theme.colors.textSecondary,
              fontWeight: 400,
              backgroundColor: theme.colors.white,
              outline: 'none', cursor: 'pointer',
            }}
          >
            <option value="">
              {(processContext.forceSecondaryAxis ?? []).length > 0 ? 'Weiteren Code hinzufügen...' : 'Automatisch (KI-Entscheidung)'}
            </option>
            {REASON_CODES.filter(rc => !(processContext.forceSecondaryAxis ?? []).includes(rc.code)).map(rc => (
              <option key={rc.code} value={rc.code}>{rc.label}</option>
            ))}
          </select>
          {/* Pre-allocated pill row — always 22px, avoids layout shift */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, marginTop: 6,
            overflow: 'hidden', height: 22,
          }}>
            {(() => {
              const codes = processContext.forceSecondaryAxis ?? [];
              if (codes.length === 0) return null;
              const visible = codes.slice(0, SECONDARY_AXIS_MAX_VISIBLE.uploads);
              const overflow = codes.slice(SECONDARY_AXIS_MAX_VISIBLE.uploads);
              return (
                <>
                  {/* Removable filter chip (own click-to-remove + × affordance) — reuses
                      secondaryAxisText/Hover only, not SecondaryAxisPills, which owns markup for
                      pure display and has no concept of removing a code. */}
                  {visible.map(code => (
                    <InfoTooltip key={code} text={cm.secondaryAxisHover(code)}>
                      <span
                        onClick={() => setProcessContext(p => {
                          const next = (p.forceSecondaryAxis ?? []).filter(c => c !== code);
                          return { ...p, forceSecondaryAxis: next.length > 0 ? next : undefined };
                        })}
                        style={{
                          padding: '2px 8px', fontSize: 10, fontWeight: 500,
                          fontFamily: theme.font.family,
                          color: theme.colors.navyMid, backgroundColor: theme.colors.bgLight,
                          border: `1px solid ${theme.colors.borderLight}`,
                          borderRadius: 10, cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          animation: 'stx-fadeIn 0.3s ease',
                          transition: 'opacity 0.15s ease',
                        }}
                      >
                        {cm.secondaryAxisText(code)} &times;
                      </span>
                    </InfoTooltip>
                  ))}
                  {overflow.length > 0 && (
                    <InfoTooltip text={overflow.map(c => cm.secondaryAxisHover(c)).join(', ')}>
                      <span
                        style={{
                          padding: '2px 8px', fontSize: 10, fontWeight: 500,
                          fontFamily: theme.font.family,
                          color: theme.colors.navyMid, backgroundColor: theme.colors.bgLight,
                          border: `1px solid ${theme.colors.borderLight}`,
                          borderRadius: 10, cursor: 'default',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        +{overflow.length}
                      </span>
                    </InfoTooltip>
                  )}
                  <span
                    onClick={() => setProcessContext(p => ({ ...p, forceSecondaryAxis: undefined }))}
                    title="Alle entfernen"
                    style={{
                      marginLeft: 'auto', fontSize: 14, color: theme.colors.textSecondary,
                      cursor: 'pointer', lineHeight: 1, flexShrink: 0,
                    }}
                  >
                    &times;
                  </span>
                </>
              );
            })()}
          </div>
        </PanelSection>

        <PanelSection title={`${cm.categoryNoun} vorgeben`}>
          <select
            id="uploads-kategorie"
            name="uploads-kategorie"
            value={processContext.forcePrimaryAxis || ''}
            onChange={e => setProcessContext(p => ({
              ...p,
              forcePrimaryAxis: e.target.value || undefined,
            }))}
            style={{
              width: '100%', padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
              fontSize: 13, fontFamily: theme.font.family,
              border: `1px solid ${theme.colors.borderLight}`,
              borderRadius: theme.borderRadius.sm,
              color: processContext.forcePrimaryAxis ? theme.colors.navy : theme.colors.textSecondary,
              backgroundColor: theme.colors.white,
              outline: 'none', cursor: 'pointer',
            }}
          >
            <option value="">Automatisch (KI-Entscheidung)</option>
            {/* categoryKeys (unclassified bucket included) is DELIBERATE here and matches the legacy arm below,
                which has always listed the full CATEGORY_KEYS: this is the testgen force-category
                picker, whose job is to drive a ticket into any state the pipeline can produce —
                including the unclassified bucket — so a tester can reproduce it. ⚠ Pre-existing inconsistency,
                preserved rather than silently resolved: ReanalysisModal's force-category picker
                (a CLERK surface) excludes the unclassified bucket. Two pickers, two audiences, opposite answers;
                both byte-stable across vocabulary delivery. Worth one operator ruling, not a
                unilateral change riding a delivery task. */}
            {cm.categoryLabelsFromData
              ? cm.categoryKeys.map(id => (
                  <option key={id} value={id}>{cm.label(id)}</option>
                ))
              : CATEGORY_KEYS.map(k => (
                  <option key={k} value={k}>{k} — {CATEGORY_LABELS[k]}</option>
                ))
            }
          </select>
        </PanelSection>

        {attachments.length > 0 && (
          // Bare 'Anhänge' while cm.configLoading, exactly as TicketDetail's Anhänge section does:
          // PanelSection's title is typed `string`, so an inclusive count painted here could only be
          // CORRECTED once the counting rule settles — a content shift that would move every control
          // below this panel (Kontext, Verarbeiten) on a cold-boot deep link into ?ticket=….
          <PanelSection title={cm.configLoading ? 'Anhänge' : `Anhänge (${anhaengeCount})`}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
              {cm.configLoading ? (
                // Row COUNT is known before the flag is (attachments arrived with the ticket) — only
                // the grouping and the number depend on config, so one shimmer per known row holds
                // the settled height from the first paint. Height 20, not TicketDetail's 16: these
                // rows carry a TriStateControl, which is taller than a text-only row.
                anhaengeRows.map((row, i) => (
                  <SkeletonBlock key={`${row.name}#${i}`} height={20} />
                ))
              ) : (
                <>
                  {anhaengeVisibleRows.map(renderAnhangRow)}
                  {/* Flag OFF only: documents render above, inline rows stay controllable below the
                      subheader — never dropped. Row order (documents-then-inline) is never disturbed
                      by re-concatenation; anhaengeVisibleRows is documents-only in this posture. */}
                  {!attachmentsCountInline && anhaengeInline.length > 0 && (
                    <>
                      <div style={{
                        fontSize: 10, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase',
                        color: theme.colors.textSecondary, marginTop: theme.spacing.sm,
                      }}>
                        {ocrReasonGroupHeading('inline')}
                      </div>
                      {anhaengeInline.map(renderAnhangRow)}
                    </>
                  )}
                </>
              )}
            </div>
          </PanelSection>
        )}

        <PanelSection title="Kontext">
          <textarea
            id="uploads-kontext"
            name="uploads-kontext"
            value={processContext.context || ''}
            onChange={e => setProcessContext(p => ({ ...p, context: e.target.value || undefined }))}
            placeholder="Zusätzliche Hinweise für die KI..."
            rows={3}
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: theme.spacing.sm,
              border: `1px solid ${theme.colors.borderLight}`,
              borderRadius: theme.borderRadius.sm,
              fontSize: 12, fontFamily: theme.font.family,
              resize: 'vertical', outline: 'none',
              minHeight: 60,
            }}
          />
        </PanelSection>
      </div>

      {/* ─── PINNED actions ─────── */}
      <div style={{
        display: 'flex', gap: theme.spacing.sm,
        padding: `${theme.spacing.md}px ${theme.spacing.lg}px`,
        borderTop: `1px solid ${theme.colors.borderLight}`,
      }}>
        <button
          onClick={onProcess}
          disabled={isProcessing || isDeleting || maintenance}
          style={{
            flex: 1,
            padding: `${theme.spacing.sm + 2}px ${theme.spacing.lg}px`,
            fontSize: 13, fontWeight: 500, fontFamily: theme.font.family,
            color: theme.colors.white, backgroundColor: theme.colors.navy,
            border: 'none',
            borderRadius: theme.borderRadius.md,
            cursor: isProcessing || isDeleting || maintenance ? 'default' : 'pointer',
            opacity: isProcessing || isDeleting || maintenance ? 0.6 : 1,
            transition: 'opacity 0.15s ease',
          }}
        >
          {isProcessing ? 'Verarbeite...' : 'Verarbeiten'}
        </button>
        <button
          onClick={onDelete}
          disabled={isProcessing || isDeleting || maintenance}
          style={{
            padding: `${theme.spacing.sm + 2}px ${theme.spacing.lg}px`,
            fontSize: 13, fontWeight: 500, fontFamily: theme.font.family,
            color: theme.colors.navy, backgroundColor: theme.colors.white,
            border: `2px solid ${theme.colors.navy}`,
            borderRadius: theme.borderRadius.md,
            cursor: isDeleting || isProcessing || maintenance ? 'default' : 'pointer',
            opacity: isProcessing || isDeleting || maintenance ? 0.6 : 1,
            transition: 'color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.color = theme.colors.error; e.currentTarget.style.borderColor = theme.colors.error; }}
          onMouseLeave={e => { e.currentTarget.style.color = theme.colors.navy; e.currentTarget.style.borderColor = theme.colors.navy; }}
        >
          Löschen
        </button>
      </div>
    </div>
  );
};

export default UploadsSeite;
